import { ZfBox, ZfStack, ZfTextButton, ZfToggle } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router';
import { validate as isValidUUID } from 'uuid';
import { useLiquidityOverviewActivated } from '../../../liquidity-planner';
import { useActiveDomicile, useClient, useStoreClient, useUserDetails } from '../../api';
import { getBusinessRelationshipWithMostAssetsUnderManagement } from '../../api/generated/client';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { Domicile } from '../../enums';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { cn } from '../../utils/classNameUtils';
import { showErrorToast } from '../../utils/toastUtils';
import { wnSendTrace, wnSendTraceNavigationChange } from '../../utils/wn-event-utils';
import { DocumentCounter } from '../client-package/DocumentCounter';
import { ClientInfo } from './ClientInfo';
import { ClientSearchInput } from './ClientSearchInput';
import { DomicileSwitch } from './DomicileSwitch';
import { LanguageSwitch } from './LanguageSwitch';
import { NavWealth } from './NavWealth';
import { ReturnToSessions } from './ReturnToSessions';
import { RoadmapNavLinkBadge } from './RoadmapNavLinkBadge';
import { RoleSwitch } from './RoleSwitch';

interface PrimaryNavigationProps {
    isContentAdminRoleEnabled?: boolean;
    variant?: 'standalone' | 'simple';
}

export const PrimaryNavigation: FunctionComponent<PrimaryNavigationProps> = ({ isContentAdminRoleEnabled = true, variant = 'standalone' }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { businessRelationshipUuid, partnerUuid, basePath, navigate } = useWealthPlanningNavigate();

    const { isPresentationMode, setPresentationMode } = useYwuApp();
    const { isWealthNavigatorMFE, dispatchNavigateToClientContextEvent, dispatchReturnToSessionsEvent } = useYwuApp();
    const { data: user } = useUserDetails();
    const { data: client } = useClient(partnerUuid);
    // todo: (liquidity-activated) remove once feature is stable and officially released
    const { data: isLiquidityPlannerActivated } = useLiquidityOverviewActivated();
    const activeDomicile = useActiveDomicile();
    const storeClient = useStoreClient();

    const isActiveRoute = (url: string) => !!matchPath({ path: url, end: false }, pathname);

    const getNavItem = (title: string, url: string) => (
        <Link to={url} onClick={() => wnSendTraceNavigationChange(url, partnerUuid)}>
            <ZfTextButton size="medium" hierarchy="tertiary" className={isActiveRoute(url) ? 'btn-active' : undefined}>
                {title}
            </ZfTextButton>
        </Link>
    );

    // undefined activeDomicile is considered as Global
    const showCalculators = activeDomicile === Domicile.CH || activeDomicile === Domicile.GLOBAL || !activeDomicile;

    const handleReturnToSessions = () => {
        dispatchReturnToSessionsEvent?.();
        wnSendTrace({ feature: 'client-session', tags: { partnerUuid, action: 'view-all-sessions' } });
    };

    const handleSetPartner = useCallback(
        (selectedPartnerUuid: string) => {
            storeClient.mutate(selectedPartnerUuid, {
                onSuccess: async () => {
                    const sendSessionOpeningTrace = (action?: string, tags?: Record<string, string>) =>
                        wnSendTrace({
                            feature: 'client-session',
                            tags: { partnerUuid: selectedPartnerUuid, action: action ?? 'open-session', source: 'partner-search-input', ...tags },
                        });

                    if (isWealthNavigatorMFE) {
                        try {
                            const targetBrUuid = await getBusinessRelationshipWithMostAssetsUnderManagement({
                                path: { partnerUuid: selectedPartnerUuid },
                            }).then((result) => result.data!);
                            if (targetBrUuid) dispatchNavigateToClientContextEvent?.(targetBrUuid, selectedPartnerUuid);
                            else navigate(`/${selectedPartnerUuid}`);
                            sendSessionOpeningTrace(undefined, { brUuid: targetBrUuid ?? '' });
                        } catch (error) {
                            // @ts-expect-error Error in catch needs to be any or unknown which causes TypeScript to not recognize the response prop
                            if (error?.response && error?.response.status === 404) {
                                navigate(`/${selectedPartnerUuid}`);
                                sendSessionOpeningTrace(undefined, { responseStatus: '404' });
                            } else {
                                showErrorToast({ error, userMessage: 'Failed to open session.' });
                                sendSessionOpeningTrace('open-session-failed', { error: 'Failed to open session.' });
                            }
                        }
                    } else {
                        navigate(`/${selectedPartnerUuid}`);
                    }
                },
            });
        },
        [navigate, storeClient, isWealthNavigatorMFE, dispatchNavigateToClientContextEvent],
    );

    const onPresentationModeToggle = (e: CustomEvent) => {
        wnSendTrace({ feature: 'presentation-mode', tags: { partnerUuid: partnerUuid, action: 'change-presentation-mode', isPresentationMode: e.detail } });
        setPresentationMode(e.detail);
    };

    const showClientNeutralNavBar =
        !client && user?.currentRole && ['RM', 'WP'].includes(user.currentRole) && !isValidUUID(partnerUuid) && !businessRelationshipUuid;

    return (
        <ZfBox className={cn('toolbar__navigation--second', variant === 'standalone' && 'toolbar__navigation--second-standalone')}>
            <div className="toolbar__navigation--second-left">
                {client?.fullName ? <ClientInfo brUuid={businessRelationshipUuid} client={client} isPresentationMode={isPresentationMode} /> : null}
                <ZfStack direction="row" alignment="center">
                    {showClientNeutralNavBar && (
                        <>
                            {getNavItem(t('navHome'), `${basePath}/topics`)}
                            {getNavItem(t('navSessions'), `${basePath}/sessions`)}
                            {showCalculators && getNavItem(t('navCalculators'), `${basePath}/calculators`)}
                        </>
                    )}
                    {!!client && (
                        <>
                            {getNavItem(t('navHome'), `${basePath}/${client.partnerUuid}/topics`)}
                            {getNavItem(t('navLifeplan'), `${basePath}/${client.partnerUuid}/events`)}
                            <NavWealth
                                title={t('navWealth')}
                                url={`${basePath}/${client.partnerUuid}/wealth`}
                                isActive={isActiveRoute(`${basePath}/${client.partnerUuid}/wealth`)}
                            />
                            {getNavItem(t('navScenarios'), `${basePath}/${client.partnerUuid}/scenarios`)}
                            {isLiquidityPlannerActivated ? getNavItem(t('navLiquidityPlanner'), `${basePath}/${client.partnerUuid}/liquidity-planner`) : null}
                            {getNavItem(t('navFamilyTree'), `${basePath}/${client.partnerUuid}/family-tree`)}
                            <RoadmapNavLinkBadge>{getNavItem(t('navRoadmap'), `${basePath}/${client.partnerUuid}/roadmap`)}</RoadmapNavLinkBadge>
                            {showCalculators && getNavItem(t('navCalculators'), `${basePath}/${client.partnerUuid}/calculators`)}
                        </>
                    )}
                </ZfStack>
            </div>
            <ZfStack direction="row" alignment="center" spacing="extra-small">
                {client && user?.currentRole && ['RM', 'WP'].includes(user.currentRole) && (
                    <ZfToggle label={t('settingsPresentationMode')} checked={isPresentationMode} onToggleChange={onPresentationModeToggle} />
                )}
                {showClientNeutralNavBar && <ClientSearchInput onSetPartner={handleSetPartner} />}
                {showClientNeutralNavBar && <DomicileSwitch />}
                <LanguageSwitch />
                {client && <DocumentCounter client={client} />}
                {!!client && isWealthNavigatorMFE && <ReturnToSessions onReturnToSessions={handleReturnToSessions} />}
                {showClientNeutralNavBar && <RoleSwitch isContentAdminRoleEnabled={isContentAdminRoleEnabled} />}
            </ZfStack>
        </ZfBox>
    );
};
